import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import PartnerShopSection from "@/components/public/PartnerShopSection";
import SafeImage from "@/components/SafeImage";

/**
 * Phase 13.7 — Collection page with optional in-page subcategory filter.
 *
 * For /collections/lifted-accessories, surface filter chips for the common
 * accessory subcategories (bongs, grinders, etc.). The filter narrows the
 * displayed products in-place — customer stays inside Lifted Accessories.
 */

const ACCESSORY_SUBFILTERS = [
  { key: "all", label: "All", match: () => true },
  {
    key: "bongs", label: "Bongs",
    match: (p) => /\bbong/i.test(p.title) || /\bbong/i.test(p.tags?.join(" ") || ""),
  },
  {
    key: "bubblers", label: "Bubblers",
    match: (p) => /\bbubbler/i.test(p.title) || /\bbubbler/i.test(p.tags?.join(" ") || ""),
  },
  {
    key: "grinders", label: "Grinders",
    match: (p) => /\bgrinder/i.test(p.title) || /\bgrinder/i.test(p.tags?.join(" ") || ""),
  },
  {
    key: "hand-pipes", label: "Hand Pipes",
    match: (p) => /\b(hand[- ]?pipe|chillum|sherlock|spoon)/i.test(p.title) || /\b(hand-?pipe|pipe)\b/i.test(p.tags?.join(" ") || ""),
  },
  {
    key: "rolling", label: "Rolling Papers / Hemp",
    match: (p) =>
      /\b(rolling|paper|hemp|wrap|cone|filter|tip)/i.test(p.title) ||
      /\b(rolling|paper|hemp|wrap)/i.test(p.tags?.join(" ") || ""),
  },
  {
    key: "vaporizers", label: "Vaporizers",
    match: (p) =>
      /\b(vape|vapor|vaporizer|e-?rig|erig)/i.test(p.title) ||
      /\b(vape|vapor|vaporizer)/i.test(p.tags?.join(" ") || ""),
  },
  {
    key: "daka", label: "Daka",
    match: (p) =>
      /\bdaka/i.test(p.title) || /\bdaka/i.test(p.tags?.join(" ") || ""),
  },
  {
    key: "yhs", label: "YHS Smoke",
    match: (p) =>
      /\byhs\b/i.test(p.title) || /\byhs\b/i.test(p.tags?.join(" ") || ""),
  },
  {
    key: "glass", label: "Glass",
    match: (p) => /\bglass\b/i.test(p.title) || /\bglass\b/i.test(p.tags?.join(" ") || ""),
  },
  {
    key: "lifestyle", label: "Lifestyle Accessories",
    match: (p) => /\b(stash|lighter|tray|case|holder|jar|stand|mat)/i.test(p.title) ||
      /\b(stash|lighter|tray|case|holder|jar|stand|mat)/i.test(p.tags?.join(" ") || ""),
  },
];

// Phase 22+ — Lifted Gear subfilters driven by the structured `product_type`
// field backfilled across the catalog (t_shirt / shorts / hat / hoodie /
// other). Falls back to title regex if product_type is missing on legacy
// rows that haven't synced yet.
const GEAR_SUBFILTERS = [
  { key: "all",     label: "All",       match: () => true },
  {
    key: "t_shirt", label: "T-Shirts",
    match: (p) => p.product_type === "t_shirt" || /\bt[- ]?shirt\b|\btee\b/i.test(p.title),
  },
  {
    key: "shorts",  label: "Shorts",
    match: (p) => p.product_type === "shorts" || /\bshort(s)?\b|\bbiker\b/i.test(p.title),
  },
  {
    key: "hat",     label: "Hats",
    match: (p) => p.product_type === "hat" || /\b(hat|cap|beanie|snapback)\b/i.test(p.title),
  },
  {
    key: "hoodie",  label: "Hoodies",
    match: (p) => p.product_type === "hoodie" || /\bhood(ie|y)\b|\bsweatshirt\b/i.test(p.title),
  },
  {
    key: "other",   label: "Other",
    match: (p) => p.product_type === "other"
      || (!["t_shirt","shorts","hat","hoodie"].includes(p.product_type)
          && !/\b(t[- ]?shirt|tee|short|biker|hat|cap|beanie|snapback|hood(ie|y)|sweatshirt)\b/i.test(p.title)),
  },
];

export default function Collection() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null);
    (async () => {
      try {
        const { data } = await api.get(`/shop/collections/${slug}`);
        setData(data);
      } catch (e) {
        setErr(apiErrorMessage(e));
      }
    })();
  }, [slug]);

  const showAccessorySubfilter = slug === "lifted-accessories";
  const showGearSubfilter = slug === "lifted-gear";
  const showSubfilter = showAccessorySubfilter || showGearSubfilter;
  const activeFilter = params.get("filter") || "all";
  const SUBFILTERS = showGearSubfilter ? GEAR_SUBFILTERS : ACCESSORY_SUBFILTERS;

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!showSubfilter || activeFilter === "all") return data.products;
    const m = SUBFILTERS.find((f) => f.key === activeFilter);
    return m ? data.products.filter(m.match) : data.products;
  }, [data, showSubfilter, activeFilter, SUBFILTERS]);

  if (err)
    return (
      <div className="max-w-7xl mx-auto px-6 py-20 text-rose-300" data-testid="collection-error">
        {err}
      </div>
    );
  if (!data)
    return (
      <div className="max-w-7xl mx-auto px-6 py-20 font-bebas tracking-[0.3em] text-lifted-dim">
        LOADING…
      </div>
    );

  return (
    <div
      data-testid={`collection-${slug}`}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10"
    >
      <header className="mb-8">
        <div className="eyebrow">Collection</div>
        <h1 className="font-anton text-4xl sm:text-5xl uppercase mt-2">
          {data.title}
        </h1>
        <p className="text-lifted-dim mt-3 max-w-2xl">{data.blurb}</p>
      </header>

      {/* Monthly Box feature banner — surfaced on Lifted Gear + Accessories
          per owner directive, above filters so it never buries the grid. */}
      {(showGearSubfilter || showAccessorySubfilter) && (
        <Link
          to="/lifted-monthly-box"
          data-testid="collection-monthly-box-banner"
          className="relative overflow-hidden rounded-xl mb-8 block border border-lifted-green/30 hover:border-lifted-green/60 transition-colors"
          style={{ background: "linear-gradient(90deg, rgba(132,255,55,0.12), rgba(10,10,20,0.6))" }}
        >
          <div className="flex items-center justify-between gap-4 px-5 py-4">
            <div>
              <div className="eyebrow text-lifted-green">New · Subscription</div>
              <div className="font-anton uppercase text-xl sm:text-2xl text-white mt-1">
                Lifted <span className="italic text-lifted-green">Monthly</span> Box
              </div>
              <p className="text-xs text-white/70 mt-1">
                Curated apparel + accessories delivered monthly. Lift-Off $40 (includes membership) · Orbit $80 · Galaxy $100 · Cosmos $200 per month.
              </p>
            </div>
            <span className="btn-lifted !py-2 !px-4 !text-xs whitespace-nowrap"
                  data-testid="collection-monthly-box-cta">
              Explore the Monthly Box
            </span>
          </div>
        </Link>
      )}

      {showSubfilter && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid={showGearSubfilter ? "gear-subfilters" : "accessory-subfilters"}>
          {SUBFILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => {
                const next = new URLSearchParams(params);
                if (f.key === "all") next.delete("filter");
                else next.set("filter", f.key);
                setParams(next);
              }}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded border ${
                activeFilter === f.key
                  ? "bg-lifted-green text-black border-lifted-green font-bebas"
                  : "border-white/15 text-lifted-text hover:text-lifted-green hover:border-lifted-green/40"
              }`}
              data-testid={`subfilter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <div
          className="glass-panel p-10 text-center text-lifted-dim"
          data-testid="collection-empty"
        >
          {data.products.length === 0
            ? "No products yet — check back soon."
            : `No products match "${SUBFILTERS.find((f) => f.key === activeFilter)?.label}" — try a different filter.`}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((p) => (
            <Link
              key={p.slug}
              to={`/products/${p.slug}`}
              data-testid={`collection-product-${p.slug}`}
              className="group block"
            >
              <div className="aspect-[3/4] overflow-hidden rounded-xl bg-lifted-surface border border-white/5 relative">
                {p.media?.[0] && (
                  <SafeImage
                    src={p.media[0]}
                    alt={p.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                )}
                {p.media?.[1] && (
                  <SafeImage
                    src={p.media[1]}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                )}
                {p.badges?.length > 0 && (
                  <div className="absolute top-2 left-2 chip chip--green text-[0.6rem]">
                    {p.badges[0]}
                  </div>
                )}
              </div>
              <div className="mt-3 text-sm text-lifted-text group-hover:text-lifted-green transition-colors">
                {p.title}
              </div>
              <div className="text-xs text-lifted-mute mt-0.5">
                {(() => {
                  const c = p.price_cents ?? Math.round((p.price || 0) * 100);
                  return c ? `$${(c / 100).toFixed(2)} USD` : "Price not available";
                })()}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Iter67 Section 4 — Approved partner stores surface under both
          flagship collections so customers can also discover partner shops. */}
      {(slug === "lifted-gear" || slug === "lifted-accessories") && (
        <PartnerShopSection />
      )}
    </div>
  );
}
